// SEQ-240: the "How is this worked out" trigger and pop-up.
//
// Thin on purpose. Every decision (open state, only one open at a time, focus
// return, sheet or pop-over, placement, aria ids, the rows and the masked
// "Right now" line) lives in howWorkedOutModel.ts, which has Deno tests. This
// file renders, measures and wires browser events to that model.
//
// Desktop: an anchored pop-over in a portal that flips and clamps inside the
// viewport and follows its trigger on scroll and resize. Phones (640px and
// narrower): a bottom sheet over a backdrop that scrolls inside itself.
// Escape, a click outside, the close button or a second click on the trigger
// close it; Escape and the close button hand focus back to the trigger.
import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";
import { useSafeMode } from "../../contexts/SafeModeContext";
import {
  buildDefinitionRows,
  choosePopMode,
  closeReasonForPointerDown,
  createPopStore,
  howWorkedOutIds,
  isCloseKey,
  nextTrapIndex,
  placePopover,
  shouldReturnFocus,
  triggerLabel,
  type Placement,
  type PopMode,
} from "./howWorkedOutModel";
import type { MetricDefinition, RightNowValue } from "./metricDefinition";
import { HowWorkedOutVisibility } from "./howWorkedOutVisibility";
import "./HowWorkedOut.css";

/** One store for the whole page, so opening one pop-up closes any other. */
const popStore = createPopStore();

const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
const POPOVER_WIDTH = 340;

interface HowWorkedOutProps {
  definition: MetricDefinition;
  /** Live values for the "Right now" row. Money is masked in Safe Mode by the shared formatter. */
  rightNow?: readonly RightNowValue[];
  /** Tells two triggers with the same figure name apart in their accessible names, e.g. "in the P&L overview". */
  where?: string;
}

export function HowWorkedOut({ definition, rightNow, where }: HowWorkedOutProps) {
  // Subscribing re-renders an open pop-up when Safe Mode flips, so its
  // "Right now" money re-masks at once.
  useSafeMode();
  const enabled = useContext(HowWorkedOutVisibility);
  const ids = howWorkedOutIds(useId());
  const state = useSyncExternalStore(popStore.subscribe, popStore.getState);
  const open = state.openId === ids.dialog;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  const [mode, setMode] = useState<PopMode>("popover");
  const [placement, setPlacement] = useState<Placement | null>(null);

  // Hand focus back to the trigger when this pop-up closed for a reason that calls for it.
  useEffect(() => {
    if (wasOpen.current && !open) {
      const closed = state.lastClosed;
      if (closed?.id === ids.dialog && shouldReturnFocus(closed.reason)) {
        triggerRef.current?.focus();
      }
    }
    wasOpen.current = open;
  }, [open, state.lastClosed, ids.dialog]);

  // Close if the figure leaves the page while its pop-up is open.
  useEffect(() => () => popStore.dispatch({ type: "close", id: ids.dialog, reason: "outside" }), [ids.dialog]);

  // Mode, placement and the follow-the-trigger listeners.
  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    const measure = () => {
      const nextMode = choosePopMode(window.innerWidth);
      setMode(nextMode);
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (nextMode !== "popover" || !trigger || !panel) return;
      const rect = trigger.getBoundingClientRect();
      setPlacement(
        placePopover(
          { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
          { width: POPOVER_WIDTH, height: panel.scrollHeight },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    // Scrolling inside the panel itself never moves the trigger, so it needs no re-measure.
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) return;
      schedule();
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, mode]);

  // A phone sheet is modal: the page behind it must not scroll or take
  // focus. The document is this app's scroller, so its overflow is locked and
  // the app root made inert until the sheet closes or unmounts. The sheet and
  // its backdrop live in a portal on <body>, outside the inert root.
  const isSheet = mode === "sheet";
  useEffect(() => {
    if (!open || !isSheet) return;
    const html = document.documentElement;
    const body = document.body;
    const previous = { html: html.style.overflow, body: body.style.overflow };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    const appRoot = document.getElementById("root");
    const wasInert = appRoot?.hasAttribute("inert") ?? true;
    if (appRoot && !wasInert) appRoot.setAttribute("inert", "");
    return () => {
      html.style.overflow = previous.html;
      body.style.overflow = previous.body;
      if (appRoot && !wasInert) appRoot.removeAttribute("inert");
    };
  }, [open, isSheet]);

  // Move focus into the dialog once it is visible (its close button), so a
  // screen reader announces the dialog's name and meaning. A pop-over is
  // hidden while it is first measured, and a hidden button cannot take focus,
  // so this waits for the placement rather than for `open` alone.
  const visible = open && (mode === "sheet" || placement !== null);
  useEffect(() => {
    if (visible) panelRef.current?.querySelector<HTMLButtonElement>(".hwo-close")?.focus({ preventScroll: true });
  }, [visible]);

  // Escape and clicks outside, while open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isCloseKey(event.key)) return;
      // Swallow the key only when it was meant for this pop-up, so Escape
      // pressed in another part of the page still reaches that part too.
      const target = event.target as Node | null;
      const ours = Boolean(target && (panelRef.current?.contains(target) || triggerRef.current?.contains(target)));
      if (ours) {
        event.preventDefault();
        event.stopPropagation();
      }
      popStore.dispatch({ type: "close", id: ids.dialog, reason: "escape" });
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const reason = closeReasonForPointerDown(
        Boolean(target && panelRef.current?.contains(target)),
        Boolean(target && triggerRef.current?.contains(target)),
      );
      if (reason) popStore.dispatch({ type: "close", id: ids.dialog, reason });
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, ids.dialog]);

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || !panelRef.current) return;
    const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const next = nextTrapIndex(focusables.indexOf(document.activeElement as HTMLElement), focusables.length, event.shiftKey);
    if (next < 0) return;
    event.preventDefault();
    focusables[next].focus();
  };

  const rows = open ? buildDefinitionRows(definition, rightNow) : [];
  const panelStyle =
    !isSheet && placement
      ? {
          top: placement.top,
          left: placement.left,
          width: placement.width,
          maxHeight: placement.maxHeight,
          transformOrigin: `${placement.originX}px ${placement.originY}px`,
        }
      : undefined;

  // Switched off for this subtree (the widget editor's preview): render nothing.
  if (!enabled) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="hwo-trigger"
        aria-label={triggerLabel(definition.name, where)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? ids.dialog : undefined}
        onClick={(event) => {
          // Never let the click reach a sortable header or a clickable card around the trigger.
          event.stopPropagation();
          popStore.dispatch({ type: "toggle", id: ids.dialog });
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") event.stopPropagation();
        }}
      >
        <Info size={14} strokeWidth={2} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <>
            {isSheet && <div className="hwo-backdrop" aria-hidden="true" />}
            <div
              ref={panelRef}
              id={ids.dialog}
              role="dialog"
              aria-modal={isSheet}
              aria-labelledby={ids.title}
              aria-describedby={ids.description}
              tabIndex={-1}
              className={`hwo-panel ${isSheet ? "hwo-panel--sheet" : "hwo-panel--popover"}`}
              data-placed={isSheet || placement ? "true" : "false"}
              style={panelStyle}
              onKeyDown={trapFocus}
            >
              <div className="hwo-head">
                <h2 id={ids.title} className="hwo-title">
                  {definition.name}
                </h2>
                <button
                  type="button"
                  className="hwo-close"
                  aria-label="Close"
                  onClick={() => popStore.dispatch({ type: "close", id: ids.dialog, reason: "close-button" })}
                >
                  <X size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
              <dl className="hwo-rows">
                {rows.map((row) => (
                  <div key={row.key} className="hwo-row">
                    <dt>{row.label}</dt>
                    <dd id={row.key === "meaning" ? ids.description : undefined}>
                      {row.lines.length === 1 ? (
                        row.lines[0]
                      ) : (
                        <ul>
                          {row.lines.map((line, index) => (
                            <li key={`${index}-${line}`}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
